/**
 * FlexibleCRUD entity definition for AI Studio agent instances (My instances).
 */

import React from 'react';
import { EntityDefinition } from '@/types/crud';
import { InstanceStatusSelect } from '@/components/ai-studio/instances/InstanceStatusSelect';
import type { StudioBindingStatus } from '@/types/aiStudioAgent';

export interface AiStudioInstanceEntityHandlers {
  onStatusChange?: () => void;
  onStatusError?: () => void;
}

const IDENTITY_CELL_CLASS = 'min-w-0 truncate';
const COMPACT_CELL_CLASS = 'min-w-0 whitespace-nowrap';
/** Version, Updated, Status, and Actions share equal width (10% each). */
const TRAILING_COLUMN_WIDTH = '10%';

function truncateText(text: string): React.ReactNode {
  return React.createElement('span', { className: 'block truncate', title: text }, text);
}

export function createAiStudioInstanceEntity(
  handlers: AiStudioInstanceEntityHandlers = {}
): EntityDefinition {
  return {
    name: 'aiStudioInstances',
    displayName: 'Agent instances',
    displayNameSingular: 'Agent instance',
    apiEndpoint: '/ai-studio/instances',
    routePath: '/ai-studio/instances',
    idField: 'id',
    nameField: 'instanceName',
    searchable: true,
    sortable: true,
    pageSize: 20,
    copyable: false,
    deletable: true,
    disableHoverTooltip: true,
    actionsColumnWidth: TRAILING_COLUMN_WIDTH,
    searchFields: ['instanceName', 'agentName', 'agentCode', 'packName', 'packCode'],
    defaultSort: { field: 'updatedAt', direction: 'desc' },
    fields: [
      { key: 'id', label: 'ID', type: 'text', hidden: true, order: 0 },
      { key: 'instanceName', label: 'Instance', type: 'text', required: true, order: 1 },
      { key: 'agentName', label: 'Base agent', type: 'text', order: 2 },
      { key: 'packName', label: 'Industry pack', type: 'text', order: 3 },
      { key: 'versionNumber', label: 'Version', type: 'number', order: 4 },
      { key: 'status', label: 'Status', type: 'select', order: 5 },
      { key: 'updatedAt', label: 'Updated', type: 'datetime', order: 6 },
    ],
    tableColumns: [
      {
        key: 'instanceName',
        label: 'aiStudio.instances.columnName',
        sortable: true,
        width: '20%',
        cellClassName: IDENTITY_CELL_CLASS,
        render: (value: unknown) => {
          const text = typeof value === 'string' && value.trim() ? value.trim() : '—';
          return text === '—' ? text : truncateText(text);
        },
      },
      {
        key: 'agentName',
        label: 'aiStudio.instances.columnAgent',
        sortable: true,
        width: '22%',
        cellClassName: IDENTITY_CELL_CLASS,
        render: (value: unknown, item?: Record<string, unknown>) => {
          const name = typeof value === 'string' ? value.trim() : '';
          const code = typeof item?.['agentCode'] === 'string' ? item['agentCode'].trim() : '';
          if (!name) return '—';
          const text = code ? `${name} (${code})` : name;
          return truncateText(text);
        },
      },
      {
        key: 'packName',
        label: 'aiStudio.instances.columnPack',
        sortable: true,
        width: '18%',
        cellClassName: IDENTITY_CELL_CLASS,
        render: (value: unknown, item?: Record<string, unknown>) => {
          const packName = typeof value === 'string' ? value.trim() : '';
          const packCode = typeof item?.['packCode'] === 'string' ? item['packCode'].trim() : '';
          const text = packName || packCode || '—';
          return text === '—' ? text : truncateText(text);
        },
      },
      {
        key: 'versionNumber',
        label: 'aiStudio.instances.columnVersion',
        sortable: true,
        width: TRAILING_COLUMN_WIDTH,
        cellClassName: COMPACT_CELL_CLASS,
        render: (value: unknown) => (value != null ? `v${value}` : '—'),
      },
      {
        key: 'updatedAt',
        label: 'aiStudio.instances.columnUpdated',
        sortable: true,
        width: TRAILING_COLUMN_WIDTH,
        cellClassName: COMPACT_CELL_CLASS,
        render: (value: string) => (value ? new Date(value).toLocaleDateString() : '—'),
      },
      {
        key: 'status',
        label: 'aiStudio.instances.filterStatus',
        sortable: true,
        width: TRAILING_COLUMN_WIDTH,
        cellClassName: 'min-w-0',
        render: (value: unknown, item?: Record<string, unknown>) => {
          const id = item?.['id'];
          if (!id || !value) return '—';
          const instanceName =
            typeof item?.['instanceName'] === 'string' ? item['instanceName'] : undefined;
          return React.createElement(InstanceStatusSelect, {
            instanceId: String(id),
            status: value as StudioBindingStatus,
            instanceName,
            onUpdated: handlers.onStatusChange,
            onError: handlers.onStatusError,
          });
        },
      },
    ],
  };
}

/** Default entity (no row status callbacks). */
export const aiStudioInstanceEntity = createAiStudioInstanceEntity();
